import { activityHelp } from '../modules/activity/help';
import { dashboardHelp } from '../modules/dashboard/help';
import { glossaryHelp } from '../modules/glossary/help';
import { importsHelp } from '../modules/imports/help';
import { keyLinksHelp } from '../modules/key-links/help';
import { keysHelp } from '../modules/keys/help';
import { missingKeysHelp } from '../modules/missing-keys/help';
import { packagesHelp } from '../modules/packages/help';
import { packageImportsHelp } from '../modules/packages/help.imports';
import { packageKeysHelp } from '../modules/packages/help.keys';
import { packageSettingsHelp } from '../modules/packages/help.settings';
import { suggestionsHelp } from '../modules/packages/help.suggestions';
import { projectsHelp } from '../modules/projects/help';
import { projectSettingsHelp } from '../modules/projects/help.settings';
import { projectSuggestionsHelp } from '../modules/projects/help.suggestions';
import { releasesHelp } from '../modules/releases/help';
import { searchHelp } from '../modules/search/help';
import { apiTokensHelp } from '../modules/settings/help.api-tokens';
import { localesHelp } from '../modules/settings/help.locales';
import { phraseSyncHelp } from '../modules/settings/help.phrase-sync';
import { publishTargetsHelp } from '../modules/settings/help.publish-targets';
import { usersHelp } from '../modules/settings/help.users';
import type { PageHelp } from './types';

/**
 * Route → page guide (spec §9.5). Every page owns a guide next to it (`modules/<x>/help.ts`, or
 * `help.<page>.ts` for a second page in the same folder); a route not listed here has no "?".
 *
 * Keep this file free of React/UI imports: `scripts/build-user-manual.mjs` and the browser audit
 * import it directly, and a UI import would break both.
 */
export const HELP_REGISTRY: Record<string, PageHelp> = {
  '/': dashboardHelp,
  '/projects': projectsHelp,
  '/projects/:code/keys': keysHelp,
  '/projects/:code/linked-keys': keyLinksHelp,
  '/projects/:code/suggestions': projectSuggestionsHelp,
  '/projects/:code/settings': projectSettingsHelp,
  '/projects/:code/releases': releasesHelp,
  '/projects/:code/missing': missingKeysHelp,
  '/projects/:code/imports': importsHelp,
  '/projects/:code/activity': activityHelp,
  '/packages': packagesHelp,
  '/packages/:code/keys': packageKeysHelp,
  '/packages/:code/imports': packageImportsHelp,
  '/packages/:code/settings': packageSettingsHelp,
  '/packages/:code/suggestions': suggestionsHelp,
  '/glossary': glossaryHelp,
  '/search': searchHelp,
  '/settings/users': usersHelp,
  '/settings/api-tokens': apiTokensHelp,
  '/settings/locales': localesHelp,
  '/settings/publish-targets': publishTargetsHelp,
  '/settings/phrase-sync': phraseSyncHelp,
};

const isPattern = (route: string): boolean => route.includes('/:');

/** Segment-wise match, where `:param` accepts any single non-empty segment. */
export const matchesRoute = (route: string, pathname: string): boolean => {
  const pattern = route.split('/');
  const actual = pathname.split('/');
  if (pattern.length !== actual.length) return false;
  return pattern.every((segment, i) =>
    segment.startsWith(':') ? (actual[i] ?? '').length > 0 : segment === actual[i],
  );
};

/**
 * The guide for a path: an exact route first, then a `:param` pattern (fewest params wins).
 *
 * Deliberately no prefix fallback: `/packages` must not answer for `/packages/x/keys`. A page
 * showing its parent's guide tells the reader about controls that are not on the screen — and
 * every route the router serves has a guide of its own (`registry.test.ts` proves it).
 */
export const helpForPath = (pathname: string): PageHelp | null => {
  const exact = HELP_REGISTRY[pathname];
  if (exact) return exact;

  const pattern = Object.keys(HELP_REGISTRY)
    .filter((route) => isPattern(route) && matchesRoute(route, pathname))
    .sort((a, b) => a.split('/:').length - b.split('/:').length)[0];
  return pattern ? (HELP_REGISTRY[pattern] ?? null) : null;
};
